import { Link } from "react-router-dom";
import { Diamond } from "lucide-react";
import logoIcon from "@/assets/logo-icon.png";
import LogoIcon from "@/components/LogoIcon";
import { Separator } from "@/components/ui/separator";

const Footer = () => {
  return (
    <footer className="bg-gradient-noir border-t border-primary/15 mt-auto relative overflow-hidden">
      {/* Top glow accent */}
      <div className="glow-line absolute top-0 left-0 right-0 opacity-60" />

      {/* Decorative ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-48 bg-primary/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 pt-20 pb-12 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8 mb-16">
          {/* Brand Section */}
          <div className="space-y-6 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 group">
              <LogoIcon className="w-10 h-10 group-hover:scale-105 transition-all duration-500 drop-shadow-[0_0_12px_hsl(var(--primary)/0.6)]" />
              <span className="text-2xl font-display font-semibold luxury-text">
                Art Experto
              </span>
            </Link>
            <p className="text-sm text-secondary-foreground/55 leading-relaxed font-light">
              Your intelligent platform for art discovery, analysis, and collection. 
              Where passion meets precision.
            </p>
            <div className="flex items-center gap-3">
              <div className="glow-line w-10 opacity-60" />
              <span className="text-2xs text-elegant tracking-luxury text-secondary-foreground/35">Est. 2024</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-elegant text-xs tracking-elegant text-primary/70 mb-6 flex items-center gap-2">
              <Diamond className="h-2.5 w-2.5" />
              Quick Links
            </h3>
            <ul className="space-y-4">
              {[
                { to: "/gallery", label: "Gallery" },
                { to: "/marketplace", label: "Marketplace" },
                { to: "/art-analysis", label: "Art Analysis" },
                { to: "/art-auction", label: "Art Auction" },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="text-sm text-secondary-foreground/55 hover:text-primary transition-colors duration-300 font-light group flex items-center gap-2"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-gradient-gold transition-all duration-300 overflow-hidden" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-elegant text-xs tracking-elegant text-primary/70 mb-6 flex items-center gap-2">
              <Diamond className="h-2.5 w-2.5" />
              Resources
            </h3>
            <ul className="space-y-4">
              {[
                { to: "/art-news", label: "Art News" },
                { to: "/chat", label: "AI Chat" },
                { to: "/premium", label: "Premium" },
                { to: "/forum", label: "Community" },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="text-sm text-secondary-foreground/55 hover:text-primary transition-colors duration-300 font-light group flex items-center gap-2"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-gradient-gold transition-all duration-300 overflow-hidden" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-elegant text-xs tracking-elegant text-primary/70 mb-6 flex items-center gap-2">
              <Diamond className="h-2.5 w-2.5" />
              Company
            </h3>
            <ul className="space-y-4">
              {[
                { to: "/about", label: "About Art Experto" },
                { to: "/about#faq", label: "FAQ" },
                { to: "/about#terms", label: "Terms & Conditions" },
                { to: "/about#privacy", label: "Privacy Policy" },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="text-sm text-secondary-foreground/55 hover:text-primary transition-colors duration-300 font-light group flex items-center gap-2"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-gradient-gold transition-all duration-300 overflow-hidden" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Decorative divider */}
        <div className="flex items-center gap-6 mb-10">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-secondary-foreground/10" />
          <div className="flex items-center gap-3">
            <Diamond className="h-2 w-2 text-primary/30" />
            <Diamond className="h-4 w-4 text-primary/50" />
            <Diamond className="h-2 w-2 text-primary/30" />
          </div>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-secondary-foreground/10" />
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-secondary-foreground/35 font-light">
            © {new Date().getFullYear()} ArtExperto.com · All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-2xs text-elegant tracking-elegant text-secondary-foreground/25">
              Crafted with passion for art
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default Footer;